import { SCHCTemplateFormatI } from '.';

import SCHCTemplate from 'shared/models/SCHCTemplate';

// SCHCTemplateFormatFormValuesC generate the expected
// SCHC Template API Body format from
// React Hooks Form format
class SCHCTemplateFormatFormToAPI implements SCHCTemplateFormatI<
  SCHCTemplate.Form.ValuesT,
  SCHCTemplate.API.BodyT,
  SCHCTemplate.API.RuleT
> {
  input: SCHCTemplate.Form.ValuesT | null = null;

  output: SCHCTemplate.API.BodyT | null = null;

  constructor(input: SCHCTemplate.Form.ValuesT) {
    this.input = input;
    this.generate();
  }

  generate = () => {
    this.output = {
      /* General information */
      name: this.input?.name,
      sCHCTemplateID: this.input?.sCHCTemplateID,
      nonMatchingRule: this.input?.nonMatchingRule,
      /* Device type */
      unruly: this.input?.unruly,
      legacy: this.input?.legacy,
      /* Template context */
      description: this.input?.description,
      /* Rules */
      deviceSchcRules: this.rules(),
      /* === */
      createdBy: this.input?.createdBy,
      payloadPattern: this.input?.payloadPattern,
    } as unknown as SCHCTemplate.API.BodyT;
  }

  private fragmentation = (formRule: SCHCTemplate.Form.RuleT):
    SCHCTemplate.API.Fragmentation.RuleT | null => {
    if (formRule.ruleType === SCHCTemplate.FRAGMENTATION_RULE) {
      const formFrag = formRule.fragmentation;
      return {
        ruleId: +formRule.ruleId,
        fragmentationParam: {
          mode: formFrag?.mode,
          ruleIdSize: formFrag?.ruleIdSize,
          dTagSize: formFrag?.dTagSize,
          inactivityTime: formFrag?.inactivityTime,
          micAlgo: formFrag?.micAlgo,
          winSize: formFrag?.winSize,
          retransmissionTime: formFrag?.retransmissionTime,
          all1RetransmissionTime: formFrag?.all1RetransmissionTime,
          wSize: formFrag?.wSize,
          maxAck: formFrag?.maxAck,
          dTagReleaseTime: formFrag?.dTagReleaseTime,
          enableShortSessionRetransmissionTime: formFrag?.enableShortSessionRetransmissionTime,
          tileSize: formFrag?.tileSize,
          aoeSpecs: {
            disableAll1Payload: formFrag?.disableAll1Payload,
            sendAckEveryWindow: formFrag?.sendAckEveryWindow,
            disableBorrowPenultimateTile: undefined,
          } as SCHCTemplate.API.Fragmentation.AOESpecs,
          optaoeSpecs: {
            enableImplicitAck: formFrag?.enableImplicitAck,
            sendAckEveryWindow: formFrag?.optSendAckEveryWindow,
          } as SCHCTemplate.API.Fragmentation.OptAOESpecs,
          qos: formFrag?.qos,
        } as SCHCTemplate.API.Fragmentation.ParamT,
        direction: formFrag?.direction,
      };
    }
    return null;
  }

  private generateRuleEntries = (formRuleFields: Array<SCHCTemplate.Form.CompressionItem>):
    Array<SCHCTemplate.API.Compression.Flow.RuleEntriesT> => {
    const ruleEntries: SCHCTemplate.API.Compression.Flow.RuleEntriesT[] = [];
    formRuleFields.forEach((field) => {
      let { targetValue } = field;
      /*
        Fields visible and editable :
        'targetValue' will be stored in field.value (text field in form)
        So we need to affect the value of field.value into targetValue
      */
      if (field.value) {
        targetValue = [field.value];
      }
      ruleEntries.push({
        ...field,
        targetValue, // Only targetValue can be different than the default value
      } as SCHCTemplate.API.Compression.Flow.RuleEntriesT);
    });
    return ruleEntries;
  }

  private compression = (formRule: SCHCTemplate.Form.RuleT):
    SCHCTemplate.API.Compression.RuleT | null => {
    if (formRule.ruleType === SCHCTemplate.COMPRESSION_RULE) {
      return {
        ruleId: +formRule.ruleId,
        flows: [
          {
            flowId: 0,
            ruleEntries: this.generateRuleEntries(
              formRule.fields as SCHCTemplate.Form.CompressionItem[],
            ),
          } as SCHCTemplate.API.Compression.Flow.ItemT,
        ] as Array<SCHCTemplate.API.Compression.Flow.ItemT>,
        stack: {
          stackType: formRule.stack,
        } as SCHCTemplate.API.Compression.StackT,
      } as SCHCTemplate.API.Compression.RuleT;
    }
    return null;
  }

  rules = (): Array<SCHCTemplate.API.RuleT> => (
    // eslint-disable-next-line arrow-body-style
    (this.input as SCHCTemplate.Form.ValuesT)
      .rules?.map((rule: SCHCTemplate.Form.RuleT) => ({
        ruleType: rule.ruleType,
        fragmentationRule: this.fragmentation(rule),
        compressionRule: this.compression(rule),
      } as SCHCTemplate.API.RuleT))
  );

  get generatedForm(): SCHCTemplate.API.BodyT | null {
    return this.output;
  }
}

export default SCHCTemplateFormatFormToAPI;
